import {
  Component,
  Input,
  Output,
  EventEmitter,
  computed,
  forwardRef,
  signal,
  ContentChild,
  TemplateRef,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { cva } from '../utils/class-variance.util';
import { ThemeSection } from '../services/theme.service';
import { IconGlassComponent } from '../icon/icon-glass.component';
import { RadioOption, RadioVariant, RadioSize, RadioLayout } from '../models/radio.model';

export { RadioOption, RadioVariant, RadioSize, RadioLayout } from '../models/radio.model';

const radioGroupVariants = cva({
  base: ['flex gap-3 transition-all duration-300 ease-out'].join(' '),
  variants: {
    layout: {
      vertical: 'flex-col',
      horizontal: 'flex-row flex-wrap',
      grid: 'grid grid-cols-2 gap-1',
    },
    size: { sm: 'gap-2', md: 'gap-3', lg: 'gap-1', xl: 'gap-6' },
  },
  defaultVariants: { layout: 'vertical', size: 'md' },
});

const radioVariants = cva({
  base: ['relative inline-block rounded-full border-2 transition-all duration-300 ease-out cursor-pointer after:content-[""] after:absolute after:-inset-3 after:rounded-full'].join(' '),
  variants: {
    variant: {
      default: 'border-gray-300/60 bg-white/25',
      premium: 'border-primary-300 bg-primary-50/10',
    } as Record<string, string>,
    size: { sm: 'w-5 h-5', md: 'w-6 h-6', lg: 'w-8 h-8', xl: 'w-10 h-10' },
    checked: { true: 'border-primary-500 bg-primary-500/30', false: '' },
    state: { default: '', error: 'border-red-400/50', success: 'border-cyan-400/50' },
  },
  defaultVariants: { variant: 'default', size: 'md', checked: 'false', state: 'default' },
});

const radioCardVariants = cva({
  base: ['block w-full p-3 rounded-lg border-2 transition-all duration-300 ease-out cursor-pointer'].join(' '),
  variants: {
    variant: {
      default: 'border-gray-300/40 dark:border-gray-600/40 bg-white/25',
    } as Record<string, string>,
    checked: {
      true: 'border-primary-500/50 dark:border-primary-400/50 bg-primary-500/10 dark:bg-primary-500/10',
      false: '',
    },
    state: { default: '', error: 'border-red-400/50', success: 'border-cyan-400/50' },
  },
  defaultVariants: { variant: 'default', checked: 'false', state: 'default' },
});

const labelVariants = cva({
  base: ['cursor-pointer transition-all duration-200 ease-out'].join(' '),
  variants: {
    size: { sm: 'text-sm', md: 'text-base', lg: 'text-lg', xl: 'text-xl' },
    disabled: { true: 'opacity-50 cursor-not-allowed', false: 'text-gray-700 dark:text-gray-200' },
  },
  defaultVariants: { size: 'md', disabled: 'false' },
});

@Component({
  selector: 'ui-radio-glass',
  standalone: true,
  imports: [CommonModule, IconGlassComponent],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => RadioGlassComponent),
      multi: true,
    },
  ],
  templateUrl: './radio-glass.component.html',
})
export class RadioGlassComponent implements ControlValueAccessor {
  @Input() options: RadioOption[] = [];
  @Input() variant: RadioVariant = 'default';
  @Input() size: RadioSize = 'md';
  @Input() layout: RadioLayout = 'vertical';
  @Input() disabled = false;
  @Input() required = false;
  @Input() cardMode = false;
  @Input() errorMessage = '';
  @Input() successMessage = '';
  @Input() themeSection: ThemeSection = 'global';
  @Input() customCardBg?: string; // Custom background color for cards
  @Input() customBorderSize?: 'thin' | 'normal' | 'thick'; // Border thickness
  @Input() customTextSize?: 'xs' | 'sm' | 'base' | 'lg'; // Text size override
  @Input() customGap?: 'none' | 'tight' | 'normal' | 'relaxed'; // Gap between options

  @Output() valueChange = new EventEmitter<unknown>();
  @Output() focus = new EventEmitter<void>();
  @Output() blur = new EventEmitter<void>();

  @ContentChild('customAction', { read: TemplateRef }) customActionTemplate?: TemplateRef<unknown>;

  protected radioName = `radio-${Math.random().toString(36).substring(2, 11)}`;
  protected selectedValue = signal<unknown>(null);

  // ControlValueAccessor callbacks
  private onChange: (v: unknown) => void = () => {};
  private onTouched: () => void = () => {};

  iconSize = computed(() => {
    const map: Record<RadioSize, 'sm' | 'md' | 'lg'> = { sm: 'sm', md: 'sm', lg: 'md', xl: 'lg' } as const;
    return map[this.size];
  });

  computedGroupClasses = computed(() => {
    let classes = radioGroupVariants({ layout: this.layout, size: this.size });

    // Apply custom gap if provided
    if (this.customGap) {
      // Remove existing gap classes
      classes = classes.replace(/gap-\d+/g, '');
      const gapMap = {
        none: 'gap-0',
        tight: 'gap-2',
        normal: 'gap-3',
        relaxed: 'gap-4',
      };
      classes += ` ${gapMap[this.customGap]}`;
    }

    return classes;
  });

  // Public API used by tests
  public getRadioClasses(value: unknown): string {
    const state = this.errorMessage ? 'error' : this.successMessage ? 'success' : 'default';
    return radioVariants({
      variant: String(this.variant),
      size: this.size,
      checked: this.isSelected(value) ? 'true' : 'false',
      state,
    });
  }

  public getCardClasses(value: unknown): string {
    const state = this.errorMessage ? 'error' : this.successMessage ? 'success' : 'default';
    const baseClasses = radioCardVariants({
      variant: String(this.variant),
      checked: this.isSelected(value) ? 'true' : 'false',
      state,
    });

    // Apply custom background if provided
    let customClasses = '';
    if (this.customCardBg) {
      customClasses += ` ${this.customCardBg}`;
    }

    // Apply custom border size
    if (this.customBorderSize === 'thin') {
      customClasses += ' border';
    } else if (this.customBorderSize === 'thick') {
      customClasses += ' border-4';
    }
    // 'normal' uses default border-2 from baseClasses

    return baseClasses + customClasses;
  }

  public getLabelClasses(option: RadioOption): string {
    const baseClasses = labelVariants({
      size: this.size,
      disabled: this.disabled || !!option.disabled ? 'true' : 'false',
    });

    // Apply custom text size if provided
    if (this.customTextSize) {
      const textSizeMap = {
        xs: 'text-xs',
        sm: 'text-sm',
        base: 'text-base',
        lg: 'text-lg',
      };
      return `${baseClasses.replace(/text-(xs|sm|base|lg|xl)/, '')} ${textSizeMap[this.customTextSize]}`;
    }

    return baseClasses;
  }

  public getDescriptionClasses(): string {
    // Default description size is one step smaller than label
    const baseSizeMap: Record<RadioSize, string> = {
      sm: 'text-xs',
      md: 'text-xs',
      lg: 'text-sm',
      xl: 'text-base',
    };

    // If custom text size is set, use even smaller for description
    if (this.customTextSize === 'xs') {
      return 'text-xs text-gray-600 dark:text-gray-400 mt-0.5';
    } else if (this.customTextSize === 'sm') {
      return 'text-xs text-gray-600 dark:text-gray-400 mt-0.5';
    }

    return `${baseSizeMap[this.size]} text-gray-600 dark:text-gray-400 mt-1`;
  }

  public getActionButtonClasses(variant?: 'default' | 'primary' | 'danger' | 'success', disabled?: boolean): string {
    const baseClasses = disabled
      ? 'text-sm font-medium cursor-default opacity-70'
      : 'text-sm hover:underline font-medium cursor-pointer';
    const variantMap = {
      default: disabled
        ? 'text-gray-600 dark:text-gray-400'
        : 'text-gray-600 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300',
      primary: disabled
        ? 'text-primary-600 dark:text-primary-400'
        : 'text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300',
      danger: disabled
        ? 'text-red-600 dark:text-red-400'
        : 'text-red-600 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300',
      success: disabled
        ? 'text-green-600 dark:text-green-400'
        : 'text-green-600 dark:text-green-400 hover:text-green-700 dark:hover:text-green-300',
    };
    return `${baseClasses} ${variantMap[variant || 'default']}`;
  }

  public isSelected(value: unknown): boolean {
    return this.selectedValue() === value;
  }

  public getOptionId(index: number): string {
    return `${this.radioName}-option-${index}`;
  }

  public handleChange(value: unknown): void {
    if (this.disabled) return;
    this.selectedValue.set(value);
    this.onChange(value);
    this.valueChange.emit(value);
  }

  public handleKeyDown(event: KeyboardEvent, value: unknown): void {
    if (this.disabled) return;
    if (event.key === ' ' || event.key === 'Enter') {
      event.preventDefault();
      this.handleChange(value);
    } else if (event.key === 'ArrowDown' || event.key === 'ArrowRight') {
      event.preventDefault();
      this.focusNext();
    } else if (event.key === 'ArrowUp' || event.key === 'ArrowLeft') {
      event.preventDefault();
      this.focusPrevious();
    }
  }

  public onFocus(): void {
    this.focus.emit();
  }

  public onBlur(): void {
    this.onTouched();
    this.blur.emit();
  }

  private focusNext(): void {
    if (!this.options || this.options.length === 0) return;
    const currentIndex = this.options.findIndex((o) => o.value === this.selectedValue());
    const nextIndex = (currentIndex + 1) % this.options.length;
    const next = this.options[nextIndex];
    if (next && !next.disabled) this.handleChange(next.value);
  }

  private focusPrevious(): void {
    if (!this.options || this.options.length === 0) return;
    const currentIndex = this.options.findIndex((o) => o.value === this.selectedValue());
    const prevIndex = currentIndex <= 0 ? this.options.length - 1 : currentIndex - 1;
    const prev = this.options[prevIndex];
    if (prev && !prev.disabled) this.handleChange(prev.value);
  }

  // ControlValueAccessor
  writeValue(value: unknown): void {
    this.selectedValue.set(value);
  }

  registerOnChange(fn: (value: unknown) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }
}
